<template>
  <div class="explore-page">
    <Explore
      :harmonys="harmonys"
      :editor="false"
      :loading="loading"
      :hasMore="hasMore"
      @like-site="handleLike"
      @share-site="handleShare"
      @load-more="loadMore"
    />
    <LoginRegister v-model="login" @login-success="handleLoginSuccess" @register-success="handleLoginSuccess" @login-closed="login = false" />
  </div>
</template>

<script>
import axios from '@/axios.js';
import Explore from "@/components/partials/harmony/Explore/Explore.vue";

export default {
  name: "ExploreView",
  metaInfo() {
    return {
      titleTemplate: '%s - Harmony Fan',
      title: this.$t('explore.title'),
    };
  },
  components: {
    Explore,
    LoginRegister: () => import('@/components/auth/LoginRegister.vue'),
  },
  data() {
    return {
      loading: true,
      isFetching: false,
      harmonys: [],
      page: 1,
      perPage: 10,
      hasMore: true,
      totalPages: 1,
      seed: null,
      login: false,
      pendingLikeId: null,
      likedHarmonyIds: new Set(), // Set global para persistir likes entre paginas
      lang: "es",
    };
  },
  created() {
    this.checkLanguage();
    this.$root.$on("languageChanged", this.handleLanguageChange);
  },
  mounted() {
    this.fetchHarmonys();
  },
  beforeDestroy() {
    this.$root.$off("languageChanged", this.handleLanguageChange);
  },
  methods: {
    getMediaPath(harmony) {
      return harmony?.site_setting?.featured_media?.path || '';
    },
    getMediaId(harmony) {
      return harmony?.site_setting?.featured_media?.media_id || null;
    },
    filterRecentDuplicates(newHarmonys) {
      // Obtener los ultimos 6 paths ya mostrados
      const recentPaths = this.harmonys
        .slice(-6)
        .map(h => this.getMediaPath(h))
        .filter(Boolean);
      const recentSet = new Set(recentPaths);

      // Filtrar nuevos que no esten en los ultimos 6
      const filtered = newHarmonys.filter(h => {
        const path = this.getMediaPath(h);
        return !path || !recentSet.has(path);
      });

      // Si todos fueron filtrados, devolver los originales para no quedar sin contenido
      return filtered.length > 0 ? filtered : newHarmonys;
    },
    applyLikedState(harmonys) {
      // Sincronizar el estado de like local con todos los items recibidos
      harmonys.forEach(h => {
        const localLiked = this.likedHarmonyIds.has(h.id);
        if (localLiked) {
          // Forzar is_liked = true si esta en nuestro Set local
          if (!h.is_liked) {
            h.is_liked = true;
            h.likes_count = (h.likes_count || 0) + 1;
          }
        }
      });
      return harmonys;
    },
    async fetchHarmonys(append = true) {
      if (this.isFetching) return;
      this.isFetching = true;
      this.loading = true;
      try {
        // Enviar media_ids ya cargados para refrescar sus conteos de vistas
        // (registradas asincronamente por ProcessMediaView desde la carga previa).
        const refreshMediaIds = this.harmonys
          .map(h => h?.site_setting?.featured_media?.media_id)
          .filter(id => id && id > 0);
        const uniqueRefreshIds = [...new Set(refreshMediaIds)];

        const response = await axios.get('/get-explore-harmonys', {
          params: {
            page: this.page,
            per_page: this.perPage,
            seed: this.seed,
            refresh_media_ids: uniqueRefreshIds,
          }
        });

        let newHarmonys = response.data.harmonys || [];
        this.seed = response.data.seed || this.seed;

        // Refrescar contadores de vistas de slides ya cargados con los datos
        // mas recientes que el backend ya proceso desde la ultima peticion.
        const refreshedCounts = response.data.refreshed_counts || {};
        if (append && Object.keys(refreshedCounts).length > 0) {
          this.harmonys.forEach(h => {
            const mediaId = h?.site_setting?.featured_media?.media_id;
            if (mediaId && refreshedCounts[mediaId]) {
              h.media_views_count = refreshedCounts[mediaId].views;
              h.media_views_formatted = refreshedCounts[mediaId].views_formatted;
            }
          });
        }

        // Sincronizar likes del backend con nuestro Set local
        newHarmonys.forEach(h => {
          if (h.is_liked) {
            this.likedHarmonyIds.add(h.id);
          }
        });

        // Aplicar estado de likes locales a los nuevos datos
        newHarmonys = this.applyLikedState(newHarmonys);

        if (append) {
          newHarmonys = this.filterRecentDuplicates(newHarmonys);
          this.harmonys = [...this.harmonys, ...newHarmonys];
        } else {
          this.harmonys = newHarmonys;
        }

        this.totalPages = response.data.last_page || 1;
        this.hasMore = this.page < this.totalPages;
      } catch (error) {
        console.error('Error fetching explore harmonys:', error);
      } finally {
        this.loading = false;
        this.isFetching = false;
      }
    },
    loadMore() {
      if (this.isFetching) return;

      if (this.hasMore) {
        this.page++;
        this.fetchHarmonys(true);
      } else {
        // Loop infinito: nuevo seed, nueva pagina aleatoria, seguir bajando
        this.page = 1;
        this.seed = Math.floor(Math.random() * 2147483647) + 1;
        this.hasMore = true;
        this.fetchHarmonys(true);
      }
    },
    handleLike(harmonyId) {
      const user = JSON.parse(localStorage.getItem('user'));
      if (!user) {
        this.pendingLikeId = harmonyId;
        this.login = true;
        return;
      }

      // Determinar si estamos agregando o quitando el like
      const isAdding = !this.likedHarmonyIds.has(harmonyId);

      // Actualizar el Set global de likes
      if (isAdding) {
        this.likedHarmonyIds.add(harmonyId);
      } else {
        this.likedHarmonyIds.delete(harmonyId);
      }

      // Actualizar TODAS las instancias del mismo Harmony ID en el array
      this.harmonys.forEach(h => {
        if (h.id === harmonyId) {
          const wasLiked = h.is_liked;
          h.is_liked = isAdding;
          // Solo ajustar contador si el estado realmente cambio
          if (wasLiked !== isAdding) {
            h.likes_count = (h.likes_count || 0) + (isAdding ? 1 : -1);
          }
        }
      });

      // Persistir en backend
      axios.post('/like-site', { site_id: harmonyId }).catch((error) => {
        console.error('Error al dar like:', error);
        // Revertir Set y todas las instancias en caso de error
        if (isAdding) {
          this.likedHarmonyIds.delete(harmonyId);
        } else {
          this.likedHarmonyIds.add(harmonyId);
        }
        this.harmonys.forEach(h => {
          if (h.id === harmonyId) {
            const wasLiked = h.is_liked;
            h.is_liked = !isAdding;
            if (wasLiked !== !isAdding) {
              h.likes_count = (h.likes_count || 0) + (!isAdding ? 1 : -1);
            }
          }
        });
      });
    },
    handleLoginSuccess() {
      this.login = false;
      // Refrescar datos para obtener is_liked actualizado desde el backend
      this.harmonys = [];
      this.page = 1;
      this.fetchHarmonys(false);

      // Si habia un like pendiente, ejecutarlo
      if (this.pendingLikeId) {
        this.$nextTick(() => {
          this.handleLike(this.pendingLikeId);
          this.pendingLikeId = null;
        });
      }
    },
    handleShare(harmonySlug) {
      const url = `${window.location.origin}/${harmonySlug}`;
      if (navigator.share) {
        navigator.share({ title: 'Harmony', url });
      } else {
        navigator.clipboard.writeText(url);
        this.$vs?.notify?.({
          title: this.$t('explore.copiedTitle'),
          text: this.$t('explore.copiedText'),
          color: "success",
          position: "bottom-center",
          time: 3000,
        });
      }
    },
    checkLanguage() {
      const storedLocale = localStorage.getItem("locale");
      this.lang = storedLocale || "es";
    },
    handleLanguageChange(newLang) {
      this.lang = newLang;
    },
  },
};
</script>

<style scoped>
.explore-page {
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: #000;
}
</style>
